import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Button, Input } from '@bitrise/bitkit';

import { navigateToUrl } from '@/utils/navigation';
import { emailValidationRule, usernameValidationRule } from '@/utils/formValidators/rules';

import useNotification from '@/hooks/useNotification';

import { SignUpRequest } from '@/models/Auth';

import { ValidatedRevealablePasswordInput } from '@/components/RevealablePasswordInput/RevealablePasswordInput';
import { getDisplayableError, getMultipleDisplayableErrors } from '@/utils/formValidators/commonErrors';
import isRunningInSelenium from '@/utils/isRunningInSelenium';
import useSignUp from '../../hooks/useSignUp/useSignUp';
import TermsOfServiceAndPrivacyPolicy from '../../components/TermsOfServiceAndPrivacyPolicy/TermsOfServiceAndPrivacyPolicy';
import { useValidateUsername } from '../../hooks/useValidateUsername/useValidateUsername';

type SignUpFormProps = {
  prefillEmail?: string;
  forcePrefilled?: boolean;
};

type FormValues = Partial<SignUpRequest['user']>;

const SignUpForm = ({ prefillEmail, forcePrefilled }: SignUpFormProps): JSX.Element => {
  const executedByBot = isRunningInSelenium();
  const { removeNotification } = useNotification();
  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState<boolean>(false);
  const { mutate: submitSignUp, isPending, isSuccess, fieldErrors, fieldErrorsAll } = useSignUp();

  const [isServerErrorHiddenFor, setIsServerErrorHiddenFor] = useState({
    email: false,
    username: false,
    password: false,
  });

  const {
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onChange' });

  const email = watch('email');
  const username = watch('username');
  const consentAccepted = watch('isSendNewsletter', false);

  const { validationError: usernameAsyncError } = useValidateUsername(username || '');

  const resetHideServerError = () => {
    setIsServerErrorHiddenFor({
      email: false,
      username: false,
      password: false,
    });
  };

  const hideServerErrorOf = (field: keyof typeof isServerErrorHiddenFor) => () => {
    setIsServerErrorHiddenFor((prev) => ({ ...prev, [field]: true }));
  };

  const { ref: emailRef, ...emailProps } = register('email', {
    ...emailValidationRule,
    onChange: hideServerErrorOf('email'),
  });

  const { ref: usernameRef, ...usernameProps } = register('username', {
    ...usernameValidationRule,
    onChange: hideServerErrorOf('username'),
  });

  const { ref: passwordRef, ...passwordProps } = register('password', {
    required: 'Password is required.',
    onChange: hideServerErrorOf('password'),
  });

  const { ref: legalConsentRef, ...legalConsentProps } = register('isSendNewsletter', {
    required: 'Agreeing to Terms of Service and Privacy Policy is required',
  });

  useEffect(() => {
    if (prefillEmail) {
      setValue('email', prefillEmail);
    }
  }, [prefillEmail, setValue]);

  const emailErrorTextClient = getDisplayableError(errors?.email?.message, 'Email');
  const emailErrorTextServer = getDisplayableError(fieldErrors?.email?.error, 'Email');
  const emailErrorText = isServerErrorHiddenFor.email ? emailErrorTextClient : emailErrorTextServer;

  const usernameErrorTextClient = getDisplayableError(usernameAsyncError || errors?.username?.message, 'Username');
  const usernameErrorTextServer = getDisplayableError(fieldErrors?.username?.error, 'Username');
  const usernameErrorText = isServerErrorHiddenFor.username ? usernameErrorTextClient : usernameErrorTextServer;

  const passwordErrorTextClient = getDisplayableError(errors?.password?.message, 'Password');
  const passwordErrorTextServer = getMultipleDisplayableErrors(fieldErrorsAll?.password, 'Password');
  const passwordErrorText = isServerErrorHiddenFor.password ? passwordErrorTextClient : passwordErrorTextServer;

  const hasFieldError = !!emailErrorText || !!usernameErrorText || !!passwordErrorText || !consentAccepted;
  const isSubmitDisabled = hasFieldError || isPending || isSuccess || executedByBot || !passwordRequirementsMet;

  const onSubmit = handleSubmit((values) => {
    if (executedByBot) {
      return;
    }

    removeNotification();
    datadogRum.addAction('submit');

    submitSignUp(
      {
        user: {
          email,
          username,
          password: values.password,
          isSendNewsletter: !!values.isSendNewsletter,
        },
      } as SignUpRequest,
      {
        onSuccess: ({ headers: { location } }) => {
          navigateToUrl(location);
        },
        onError: resetHideServerError,
      },
    );
  });

  return (
    <Box as="form" onSubmit={onSubmit} display="flex" flexDirection="column" gap="16" noValidate>
      <Input
        placeholder="Email"
        type="email"
        autoFocus
        inputRef={emailRef}
        label="Email"
        errorText={emailErrorText}
        isRequired
        isDisabled={forcePrefilled}
        {...emailProps}
      />
      <Input
        placeholder="Username"
        inputRef={usernameRef}
        label="Username"
        errorText={usernameErrorText}
        isRequired
        {...usernameProps}
      />
      <ValidatedRevealablePasswordInput
        email={email}
        username={username}
        inputRef={passwordRef}
        label="Password"
        placeholder="Password"
        errorText={passwordErrorText}
        onValidate={setPasswordRequirementsMet}
        isRequired
        {...passwordProps}
      />
      <Box display="flex" flexDirection="column" gap="32">
        <TermsOfServiceAndPrivacyPolicy ref={legalConsentRef} {...legalConsentProps} />
        <Button isLoading={isPending} isDisabled={isSubmitDisabled} width="100%" type="submit">
          Sign up
        </Button>
      </Box>
    </Box>
  );
};

export default SignUpForm;
